import { Injectable } from '@angular/core';
import { HttpClient, HttpEvent, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { ConfigService } from '@app/core/config/config.service';
import { OnboardingIdStorage } from '@app/utils/services/onboarding-id-storage.service';
import { KybRepository } from '@app/domain/ports/kyb.repository';
import {
  KybContactInfo,
  KybCompanyInfo,
  KybCompanyTypeOption,
  KybSatInfo,
  KybLegalRepresentative,
  KybBeneficialOwner,
  KybBusinessContact,
  KybSpouseDependent,
  KybResourceProvidersToggle,
  KybResourceProvidersBulkRequest,
  KybResourceProvidersBulkResponse,
  KybBeneficiariesBulkRequest,
  KybPaymentBeneficiariesBulkRequest,
  KybOperationalInfo,
  KybPosMerchantInfo,
  KybSolicitantForm,
  KybShareholderFormData,
  KybOrgChart,
  KybClientsProvidersInfo,
  KybDocumentUrlRequest,
  KybShareholder,
  KybShareholderBulkRequest,
  KybApiResponse,
  KybProcessState,
} from '../interfaces/kyb';

/**
 * Implementación actual del puerto [[KybRepository]]. Conserva el bridge mock
 * (mediante `configService.isMockBackend`) para preservar el flujo de revisión
 * de maquetas que ya está estabilizado. La separación remote/mock en
 * data-sources independientes está prevista como follow-up — esta clase es el
 * paso intermedio (puerto definido + implementación única) para que los
 * componentes nuevos puedan depender de la abstracción sin tocar consumidores
 * existentes.
 */
@Injectable({
  providedIn: 'root'
})
export class KybApiService extends KybRepository {

  private baseUrl: string;

  constructor(
    private http: HttpClient,
    private idStorage: OnboardingIdStorage,
    private configService: ConfigService
  ) {
    super();
    this.baseUrl = `${this.configService.baseUrl}onboarding`;
  }

  // Paso 0 — Crear proceso
  startOnboarding(personType: string, product: string): Observable<any> {
    // Modo mock (revisión de maquetas): el caller guarda response.id en sessionStorage
    // para que los siguientes pasos pasen el check de getId(). MOCK_ID es un sentinel.
    if (this.configService.isMockBackend) return of({ id: 'MOCK_ID' });
    const params = new URLSearchParams({ personType, product });
    const url = `${this.baseUrl}/start?${params.toString()}`;
    return this.http.post(url, {});
  }

  // Pantalla 1 — Información de contacto
  saveContactInfo(data: KybContactInfo): Observable<any> {
    if (this.configService.isMockBackend) return of({ data: null });
    const url = `${this.baseUrl}/${this.getId()}/contact-info`;
    return this.http.post(url, data);
  }

  // Pantalla 2 (PM) — Datos de la empresa
  saveCompanyInfo(data: KybCompanyInfo): Observable<any> {
    if (this.configService.isMockBackend) return of({ data: null });
    const url = `${this.baseUrl}/${this.getId()}/company-info`;
    return this.http.post(url, data);
  }

  /**
   * Catálogo de tipos de empresa que el backend acepta en `companyType`
   * (campo requerido en POST /onboarding/{id}/company-info).
   * Estructura: { code, label } — el `code` es lo que se manda en el payload.
   */
  getCompanyTypes(): Observable<KybApiResponse<KybCompanyTypeOption[]>> {
    if (this.configService.isMockBackend) {
      return of<KybApiResponse<KybCompanyTypeOption[]>>({
        success: true,
        code: '',
        message: '',
        data: [
          { code: 'SA_CV', label: 'S.A. de C.V. — Sociedad Anónima de Capital Variable' },
          { code: 'SAPI_CV', label: 'S.A.P.I. de C.V. — Sociedad Anónima Promotora de Inversión' },
          { code: 'SRL_CV', label: 'S. de R.L. de C.V. — Sociedad de Responsabilidad Limitada' },
          { code: 'SAS', label: 'S.A.S. — Sociedad por Acciones Simplificada' },
          { code: 'OTRA', label: 'Otra' },
        ],
      });
    }
    const url = `${this.baseUrl}/catalogs/company-types`;
    return this.http.get<KybApiResponse<KybCompanyTypeOption[]>>(url);
  }

  // Pantalla 3 — Vinculación SAT
  saveSatInfo(data: KybSatInfo): Observable<any> {
    if (this.configService.isMockBackend) return of({ data: null });
    const url = `${this.baseUrl}/${this.getId()}/sat-info`;
    return this.http.post(url, data);
  }

  // Pantalla 4.A — Validar CURP (opcional)
  validateCurp(curp: string): Observable<any> {
    if (this.configService.isMockBackend) return of({ data: { valid: true } });
    const url = `${this.baseUrl}/validate-curp`;
    return this.http.post(url, { curp });
  }

  // Pantalla 4.B — Guardar representante legal
  saveLegalRepresentative(data: KybLegalRepresentative): Observable<any> {
    if (this.configService.isMockBackend) return of({ data: null });
    const url = `${this.baseUrl}/${this.getId()}/legal-representative`;
    return this.http.post(url, data);
  }

  // Pantalla 5 (PFAE) — Guardar beneficiario real
  saveBeneficialOwner(data: KybBeneficialOwner): Observable<any> {
    if (this.configService.isMockBackend) return of({ data: null });
    const url = `${this.baseUrl}/${this.getId()}/beneficial-owner`;
    return this.http.post(url, data);
  }

  // ═════════════════════════════════════════════════════════════════════
  // Endpoints nuevos (spec mayo 2026 — guía FRONTEND_INTEGRATION_GUIDE.md)
  // ═════════════════════════════════════════════════════════════════════

  /** Contacto del negocio (SUITE — 5.2 PFAE / 7 PM). */
  saveBusinessContact(data: KybBusinessContact): Observable<any> {
    if (this.configService.isMockBackend) return of({ data: null });
    const url = `${this.baseUrl}/${this.getId()}/business-contact`;
    return this.http.post(url, data);
  }

  /** Cónyuge / dependientes económicos (PFAE+SUITE — 5.3).
   *  Si `hasSpouseOrDependents=false`, el back ignora phone/email y borra
   *  automáticamente los docs INE_SPOUSE/CSF_SPOUSE. */
  saveSpouseDependent(data: KybSpouseDependent): Observable<any> {
    if (this.configService.isMockBackend) return of({ data: null });
    const url = `${this.baseUrl}/${this.getId()}/spouse-dependent`;
    return this.http.post(url, data);
  }

  /** Toggle de proveedores externos (SUITE PM / CARDS — controla si la
   *  sección está habilitada). PFAE+SUITE NO usa este toggle (siempre ≥1). */
  toggleResourceProviders(data: KybResourceProvidersToggle): Observable<any> {
    if (this.configService.isMockBackend) return of({ data: null });
    const url = `${this.baseUrl}/${this.getId()}/resource-providers/toggle`;
    return this.http.post(url, data);
  }

  /** Bulk de proveedores de recursos (SUITE — 5.5 PFAE / 7 PM).
   *  Reemplaza atómicamente la colección. Cada proveedor incluye sus s3Key
   *  previamente confirmados (sin shareholderId/owner FK). */
  bulkResourceProviders(data: KybResourceProvidersBulkRequest): Observable<KybApiResponse<KybResourceProvidersBulkResponse>> {
    if (this.configService.isMockBackend) {
      return of<KybApiResponse<KybResourceProvidersBulkResponse>>({
        success: true,
        code: 'OK',
        message: 'Mock providers bulk',
        data: {
          providers: data.providers.map((p, i) => ({
            id: 1000 + i,
            tempId: p.tempId,
            documentsLinked: p.documents.length,
          })),
        },
      });
    }
    const url = `${this.baseUrl}/${this.getId()}/resource-providers/bulk`;
    return this.http.post<KybApiResponse<KybResourceProvidersBulkResponse>>(url, data);
  }

  /** Bulk de beneficiarios (SUITE — 5.6 PFAE / 7 PM). */
  bulkBeneficiaries(data: KybBeneficiariesBulkRequest): Observable<any> {
    if (this.configService.isMockBackend) {
      return of({
        data: {
          beneficiaries: data.beneficiaries.map((b, i) => ({
            id: 2000 + i,
            tempId: b.tempId,
            documentsLinked: b.documents.length,
          })),
        },
      });
    }
    const url = `${this.baseUrl}/${this.getId()}/beneficiaries/bulk`;
    return this.http.post(url, data);
  }

  /** Bulk de beneficiarios de PAGO (CARDS — solo INE + nombre, sin %). */
  bulkPaymentBeneficiaries(data: KybPaymentBeneficiariesBulkRequest): Observable<any> {
    if (this.configService.isMockBackend) {
      return of({
        data: {
          paymentBeneficiaries: data.paymentBeneficiaries.map((b, i) => ({
            id: 3000 + i,
            tempId: b.tempId,
            documentsLinked: b.documents.length,
          })),
        },
      });
    }
    const url = `${this.baseUrl}/${this.getId()}/payment-beneficiaries/bulk`;
    return this.http.post(url, data);
  }

  /** Información operativa (SUITE — 5.7 PFAE / 7 PM). */
  saveOperationalInfo(data: KybOperationalInfo): Observable<any> {
    if (this.configService.isMockBackend) return of({ data: null });
    const url = `${this.baseUrl}/${this.getId()}/operational-info`;
    return this.http.post(url, data);
  }

  /** Información operativa POS (POS — distinto schema que SUITE). */
  savePosMerchantInfo(data: KybPosMerchantInfo): Observable<any> {
    if (this.configService.isMockBackend) return of({ data: null });
    const url = `${this.baseUrl}/${this.getId()}/pos-merchant-info`;
    return this.http.post(url, data);
  }

  /** Formulario PLD/KYC del solicitante (FORM_INFO — Paso 6 PFAE / 8 PM).
   *  Mismo endpoint para PFAE y PM; PM+SUITE incluye campos PEP extendidos. */
  saveSolicitantForm(data: KybSolicitantForm): Observable<any> {
    if (this.configService.isMockBackend) return of({ data: null });
    const url = `${this.baseUrl}/${this.getId()}/solicitant-form`;
    return this.http.post(url, data);
  }

  /** Datos del formulario PLD/KYC por accionista (PM+SUITE — FORM_INFO 8.2).
   *  Llamar una vez por cada accionista creado en SHAREHOLDERS. */
  saveShareholderFormData(data: KybShareholderFormData): Observable<any> {
    if (this.configService.isMockBackend) return of({ data: null });
    const url = `${this.baseUrl}/${this.getId()}/shareholder-form-data`;
    return this.http.post(url, data);
  }

  /** Organigrama empresarial (PM+SUITE — FORM_INFO 8.3). */
  saveOrgChart(data: KybOrgChart): Observable<any> {
    if (this.configService.isMockBackend) return of({ data: null });
    const url = `${this.baseUrl}/${this.getId()}/org-chart`;
    return this.http.post(url, data);
  }

  /** Información clientes-proveedores (PM+SUITE — FORM_INFO 8.4). */
  saveClientsProvidersInfo(data: KybClientsProvidersInfo): Observable<any> {
    if (this.configService.isMockBackend) return of({ data: null });
    const url = `${this.baseUrl}/${this.getId()}/clients-providers-info`;
    return this.http.post(url, data);
  }

  /** Documentos — Pedir URL prefirmada (spec mayo 2026: `documents/presigned-url`,
   *  antes `documents/upload-url`). FKs opcionales para listas dinámicas:
   *   - `shareholderId`: PM+SUITE (docs por accionista)
   *   - `resourceProviderId`: SUITE/CARDS (docs por proveedor)
   *   - `beneficiaryId`: SUITE (docs por beneficiario)
   *   - `paymentBeneficiaryId`: CARDS (docs por beneficiario de pago)
   *  Devuelve `{ uploadUrl, s3Key }`. El mock conserva las claves `url` y `s3Key`
   *  por compat con el código existente que las consume. */
  getDocumentUploadUrl(data: KybDocumentUrlRequest): Observable<any> {
    if (this.configService.isMockBackend) {
      const fakeKey = `mock-${Date.now()}`;
      return of({ data: { uploadUrl: `mock://${fakeKey}`, url: `mock://${fakeKey}`, s3Key: fakeKey } });
    }
    const url = `${this.baseUrl}/${this.getId()}/documents/presigned-url`;
    const body: Record<string, unknown> = {
      documentType: data.documentType,
      fileName: data.fileName,
      contentType: data.contentType,
    };
    if (data.shareholderId != null) body['shareholderId'] = data.shareholderId;
    if ((data as any).resourceProviderId != null) body['resourceProviderId'] = (data as any).resourceProviderId;
    if ((data as any).beneficiaryId != null) body['beneficiaryId'] = (data as any).beneficiaryId;
    if ((data as any).paymentBeneficiaryId != null) body['paymentBeneficiaryId'] = (data as any).paymentBeneficiaryId;
    // 2026-05-20 — ownerScope evita que docs huérfanos (sin FK) de distintas
    // secciones con mismo documentType se sobreescriban entre sí.
    if (data.ownerScope) body['ownerScope'] = data.ownerScope;
    return this.http.post(url, body);
  }

  // Documentos — PUT directo a S3
  uploadToS3(s3Url: string, file: File, contentType: string): Observable<HttpEvent<unknown>> {
    // Modo mock: emitimos un Response event directo para cerrar el stream sin subir a S3.
    if (this.configService.isMockBackend) {
      return of(new HttpResponse({ status: 200, body: null })) as Observable<HttpEvent<unknown>>;
    }
    const headers = new HttpHeaders({ 'Content-Type': contentType });
    return this.http.put(s3Url, file, {
      headers,
      reportProgress: true,
      observe: 'events'
    });
  }

  /** Devuelve una URL firmada de S3 (TTL corto) para visualizar el doc en otra
   *  pestaña. Backend: `GET /onboarding/{processId}/documents/{documentId}/download-url`
   *  → `{ data: { url } }`. El TTL típico es de pocos minutos; el navegador
   *  carga la URL directamente, sin proxy. */
  getDocumentDownloadUrl(documentId: number): Observable<any> {
    if (this.configService.isMockBackend) {
      return of({ data: { url: `mock://download/${documentId}` } });
    }
    const url = `${this.baseUrl}/${this.getId()}/documents/${documentId}/download-url`;
    return this.http.get(url);
  }

  /** Documentos — Confirmar documento. FKs opcionales para listas dinámicas
   *  (mismas que `getDocumentUploadUrl`): shareholderId, resourceProviderId,
   *  beneficiaryId, paymentBeneficiaryId.
   *
   *  2026-05-20: parámetro `ownerScope` para etiquetar docs huérfanos por
   *  sección lógica (legal_rep, society, owner, shareholder:<tempId>, etc.) y
   *  evitar colisiones entre documentTypes idénticos de secciones distintas.
   *  Si se manda una FK numérica, el back ignora el scope (la FK es la fuente
   *  de verdad). */
  confirmDocumentUpload(
    documentType: string,
    s3Key: string,
    fks?: {
      shareholderId?: number;
      resourceProviderId?: number;
      beneficiaryId?: number;
      paymentBeneficiaryId?: number;
      ownerScope?: string;
    },
  ): Observable<any> {
    if (this.configService.isMockBackend) {
      return of({ data: { s3Key, documentId: Date.now(), documentType } });
    }
    const url = `${this.baseUrl}/${this.getId()}/documents/confirm`;
    const body: Record<string, unknown> = { documentType, s3Key };
    if (fks?.shareholderId != null) body['shareholderId'] = fks.shareholderId;
    if (fks?.resourceProviderId != null) body['resourceProviderId'] = fks.resourceProviderId;
    if (fks?.beneficiaryId != null) body['beneficiaryId'] = fks.beneficiaryId;
    if (fks?.paymentBeneficiaryId != null) body['paymentBeneficiaryId'] = fks.paymentBeneficiaryId;
    if (fks?.ownerScope) body['ownerScope'] = fks.ownerScope;
    return this.http.post(url, body);
  }

  // ─── Accionistas (PM+SUITE — re-activado 2026-05-19, spec mayo 2026) ──────
  // Endpoint principal: bulk (atómico, reemplaza colección + asocia docs por s3Key).
  // CRUD individual disponible como fallback.

  /** Bulk de accionistas con sus 5 docs (INE, CSF, DOMICILIO, PODERES, SELFIE_WITH_ID).
   *  Reemplaza atómicamente la colección. Reglas backend:
   *   - Al menos un `isBeneficialOwner=true` (suman >25%)
   *   - RFC único en el lote
   *   - s3Key único entre accionistas */
  bulkCreateShareholders(data: KybShareholderBulkRequest): Observable<any> {
    if (this.configService.isMockBackend) {
      return of({
        data: {
          shareholders: data.shareholders.map((s, i) => ({
            id: 4000 + i,
            tempId: s.tempId,
            documentsLinked: s.documents.length,
          })),
        },
      });
    }
    const url = `${this.baseUrl}/${this.getId()}/shareholders/bulk`;
    return this.http.post(url, data);
  }

  /** Listar accionistas del proceso. */
  listShareholders(): Observable<any> {
    if (this.configService.isMockBackend) return of({ data: [] });
    const url = `${this.baseUrl}/${this.getId()}/shareholders/list`;
    return this.http.get(url);
  }

  /** Agregar un accionista individual (fallback al bulk). */
  createShareholder(data: KybShareholder): Observable<any> {
    if (this.configService.isMockBackend) return of({ data: { id: Date.now() } });
    const url = `${this.baseUrl}/${this.getId()}/shareholders`;
    return this.http.post(url, data);
  }

  /** Eliminar un accionista por id. */
  deleteShareholder(shareholderId: number): Observable<any> {
    if (this.configService.isMockBackend) return of({ data: null });
    const url = `${this.baseUrl}/${this.getId()}/shareholders/${shareholderId}`;
    return this.http.delete(url);
  }

  // Verificación de teléfono — enviar código SMS
  sendPhoneCode(phone: string): Observable<any> {
    if (this.configService.isMockBackend) return of({ data: null });
    const url = `${this.baseUrl}/${this.getId()}/phone/send-code`;
    return this.http.post(url, { phone });
  }

  // Verificación de teléfono — verificar código
  verifyPhoneCode(phone: string, code: string): Observable<any> {
    if (this.configService.isMockBackend) return of({ data: { verified: true } });
    const url = `${this.baseUrl}/${this.getId()}/phone/verify`;
    return this.http.post(url, { phone, code });
  }

  // Avanzar paso
  nextStep(): Observable<any> {
    // Modo mock (revisión de maquetas): no se invoca el backend.
    if (this.configService.isMockBackend) return of({ data: null });
    const url = `${this.baseUrl}/${this.getId()}/next`;
    return this.http.post(url, {});
  }

  // Enviar solicitud
  submit(): Observable<any> {
    // Modo mock (revisión de maquetas): no se invoca el backend.
    if (this.configService.isMockBackend) return of({ data: null });
    const url = `${this.baseUrl}/${this.getId()}/submit`;
    return this.http.post(url, { acceptedPrivacyPolicy: true });
  }

  // Utilitarios
  getSteps(): Observable<any> {
    if (this.configService.isMockBackend) return of({ data: [] });
    const url = `${this.baseUrl}/${this.getId()}/steps`;
    return this.http.get(url);
  }

  getCurrentStep(): Observable<any> {
    if (this.configService.isMockBackend) return of({ data: null });
    const url = `${this.baseUrl}/${this.getId()}/current-step`;
    return this.http.get(url);
  }

  /** Rehidrata el estado completo del proceso (para load inicial o F5).
   *  En modo mock, el caller (KybFlowStateService) no invoca este metodo — corta antes. */
  getState(): Observable<KybApiResponse<KybProcessState>> {
    const url = `${this.baseUrl}/${this.getId()}/state`;
    return this.http.get<KybApiResponse<KybProcessState>>(url);
  }

  getId(): string {
    return this.idStorage.get() ?? '';
  }
}
